import eventBus from './eventBus';
import { EVENT_BET_CART_UPDATED } from '../utils/constants';

/**
 * BetCartService - Singleton service for managing saved bets
 * Maximum of 10 bets can be saved
 */
class BetCartService {
    static _instance = null;
    static MAX_BETS = 10;
    static STORAGE_KEY = 'betCart';

    constructor() {
        if (BetCartService._instance) {
            return BetCartService._instance;
        }

        this.bets = this.loadBets();
        BetCartService._instance = this;
    }

    static getInstance() {
        if (!BetCartService._instance) {
            BetCartService._instance = new BetCartService();
        }
        return BetCartService._instance;
    }

    /**
     * Generate a unique ID for a bet
     * Uses scouting report ID + bet type for reliable tracking
     */
    generateBetId(bet) {
        if (!bet || !bet.type) {
            console.warn('Invalid bet data for ID generation:', bet);
            return null;
        }

        // Always include bet type in ID to ensure uniqueness
        let key;
        let isProp = bet.type === 'Prop';
        if (bet.cartKey || bet.sourceBetId) {
            key = bet.cartKey || bet.sourceBetId;
        } else if(isProp) {
            if (bet.id) {
                key = bet.id;
            } else {
                // Keep the discriminating prop fields first. Long event/scouting IDs are
                // appended so ID truncation cannot collapse every prop in one event.
                key = `Prop-${bet.player || 'unknown'}-${bet.marketType || 'unknown'}-${bet.bet || ''}-${bet.scoutingReportId || bet.eventId || ''}`;
            }
        } else {
            if (bet.id) {
                key = bet.id;
            } else {
                // For team bets (Moneyline, Spread, Totals): scoutingReportId-{betType}
                // This ensures each bet type is unique per scouting report
                if (bet.scoutingReportId) {
                    key = `${bet.scoutingReportId}-${bet.type}`;
                } else {
                    // Fallback: Include bet type + event + bet text to ensure uniqueness
                    key = `${bet.type}-${bet.event || 'unknown'}-${bet.bet || ''}`;
                }
            }
        }
        
        // Generate a stable ID without relying on btoa, which fails on Unicode text.
        let hash = 0;
        for (let index = 0; index < key.length; index += 1) {
            hash = ((hash << 5) - hash) + key.charCodeAt(index);
            hash |= 0;
        }

        const safePrefix = key.replace(/[^a-zA-Z0-9]/g, '').substring(0, 18);
        return `${safePrefix}${Math.abs(hash).toString(36)}`.substring(0, 40);
    }

    /**
     * Load bets from localStorage
     */
    loadBets() {
        try {
            const savedBets = localStorage.getItem(BetCartService.STORAGE_KEY);
            if (savedBets) {
                return JSON.parse(savedBets);
            }
        } catch (error) {
            console.error('Error loading bets from localStorage:', error);
        }
        return [];
    }

    /**
     * Save bets to localStorage
     */
    saveBets() {
        try {
            localStorage.setItem(BetCartService.STORAGE_KEY, JSON.stringify(this.bets));
        } catch (error) {
            console.error('Error saving bets to localStorage:', error);
            // Handle quota exceeded error
            if (error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded. Clearing old bets.');
                this.bets = this.bets.slice(-BetCartService.MAX_BETS);
                try {
                    localStorage.setItem(BetCartService.STORAGE_KEY, JSON.stringify(this.bets));
                } catch (e) {
                    console.error('Failed to save after clearing:', e);
                }
            }
        }
    }

    /**
     * Add a bet to the cart
     * @param {Object} betData - Bet data object
     * @returns {Object} { success: boolean, message: string }
     */
    addBet(betData) {
        // Validate bet data
        if (!betData || !betData.type || !betData.bet || !betData.event) {
            return { success: false, message: 'Invalid bet data' };
        }

        // Check if cart is full
        if (this.bets.length >= BetCartService.MAX_BETS) {
            return { success: false, message: `Maximum of ${BetCartService.MAX_BETS} bets allowed` };
        }

        // Generate bet ID
        const betId = this.generateBetId(betData);
        
        // Check if bet already exists
        if (this.isBetSaved(betId)) {
            return { success: false, message: 'Bet already saved' };
        }

        // Add bet with ID and timestamp
        const betWithId = {
            ...betData,
            id: betId,
            savedAt: new Date().toISOString()
        };

        this.bets.push(betWithId);
        this.saveBets();
        
        // Emit event to notify all SaveBetButton components
        eventBus.emit(EVENT_BET_CART_UPDATED, { action: 'added', betId });
        
        return { success: true, message: 'Bet saved successfully', betId };
    }

    /**
     * Remove a bet from the cart
     * @param {string} betId - Bet ID
     * @returns {boolean} Success status
     */
    removeBet(betId) {
        const index = this.bets.findIndex(bet => bet.id === betId);
        if (index !== -1) {
            this.bets.splice(index, 1);
            this.saveBets();
            
            // Emit event to notify all SaveBetButton components
            eventBus.emit(EVENT_BET_CART_UPDATED, { action: 'removed', betId });
            
            return true;
        }
        return false;
    }

    /**
     * Get all saved bets
     * @returns {Array} Array of saved bets
     */
    getAllBets() {
        return [...this.bets];
    }

    /**
     * Clear all bets from cart
     */
    clearCart() {
        const removedBetIds = this.bets.map(bet => bet.id);
        this.bets = [];
        this.saveBets();
        
        // Emit event to notify all SaveBetButton components
        eventBus.emit(EVENT_BET_CART_UPDATED, { action: 'cleared', betIds: removedBetIds });
    }

    /**
     * Check if a bet is already saved
     * @param {string} betId - Bet ID
     * @returns {boolean}
     */
    isBetSaved(betId) {
        return this.bets.some(bet => bet.id === betId);
    }

    /**
     * Get count of saved bets
     * @returns {number}
     */
    getBetCount() {
        return this.bets.length;
    }

    /**
     * Check if cart is full
     * @returns {boolean}
     */
    isFull() {
        return this.bets.length >= BetCartService.MAX_BETS;
    }
}

export default BetCartService;
